import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Button, Col, ControlLabel, Form, FormControl, FormGroup, Glyphicon, HelpBlock, Panel} from 'react-bootstrap';
import EditAssessmentSubsection from './EditAssessmentSubsection';
import {withRouter} from 'react-router';
import {
  addSubsection,
  changeAssessmentName,
  changeSubsectionTitle,
  clearAssessmentForm,
  removeSubsection,
} from '../../utils/actions/createAssessment';
import {connect} from 'react-redux';
import validator from 'validator';
import GreenLeafApi from '../../utils/GreenLeafApi';
import {toast, ToastContainer} from 'react-toastify';
import ErrorComponent from '../../ErrorComponent';

class EditAssessment extends Component {

  constructor(props) {
    super(props);

    this.state = {
      error: [],
      errors: {}
    };
  }

  validate = () => {
    let errors = {};

    if (validator.isEmpty(this.props.assessment.name)) {
      errors['name'] = 'This is a required field.';
    }

    this.props.assessment.subsections.forEach((ss, i) => {
      if (!ss.questions || ss.questions.length === 0) {
        errors[i] = 'This subsection has no questions!';
      } else if (validator.isEmpty(ss.name)) {
        errors[i] = 'Subsections must have a name.';
      }
    });

    this.setState({errors});
    return Object.keys(errors).length === 0;
  };

  formatAssessment = () => {
    let assessment = {
      name: this.props.assessment.name,
      subsections: []
    };

    this.props.assessment.subsections.forEach((ss, i) => {
      let subsection = {
        name: ss.name,
        questions: []
      };

      this.props.assessment.subsections[i].questions.forEach(q => {
        let question = {
          name: q.name,
          weight: q.weight,
          answers: []
        };
        delete q.name;
        delete q.weight;
        question.answers = Object.values(q);

        subsection.questions.push(question);
      });

      assessment.subsections.push(subsection);
    });

    return assessment;
  };

  submitAssessment = async event => {
    event.preventDefault();

    if (this.validate() && this.props.assessment.subsections.length > 0) {
      const assessment = this.formatAssessment();

      try {
        await GreenLeafApi.Assessment.create(assessment);
        toast.success('Assessment successfully created!', {
          position: 'top-center',
          autoClose: 1500,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
        });

        this.props.clearForm();
      } catch (e) {
        this.setState({error: e.errors});
      }
    }
  };

  onCancel = () => {
    this.props.history.push('/assessments');
  };

  getAssessmentPoints = () => {
    let val = 0;

    this.props.assessment.subsections.forEach(subsection => {
      if (subsection.questions) {
        subsection.questions.forEach(q => {
          val += (+q.weight || 1) * Object.keys(q).filter(x => !isNaN(+x)).length;
        });
      }
    });

    return val;
  };

  render() {
    let subsections = this.props.assessment.subsections.map((s, i) => {
      return (
        <div key={i} style={{ display: 'flex', flexDirection: 'row', margin: '20px 0 20px 0' }}>
          <EditAssessmentSubsection id={i} name={s.name} error={this.state.errors[i]} questions={s.questions} onChange={(event) => { let value = event.target.value; this.props.changeSubsectionTitle(value, i); }}/>
          <Button data-cy={`remove-${i}`} onClick={() => this.props.removeSubsection(i)} style={{ marginLeft: '10px', height: '40px', marginTop: '7.5px' }}>
            <Glyphicon glyph={'remove-circle'}/>
          </Button>
        </div>
      );
    });

    return (
      <div>
        <ErrorComponent error={this.state.error}/>
        <h2 className='ta-center'>Create Assessment</h2>
        <br/>

        <Form horizontal onSubmit={this.submitAssessment}>
          <FormGroup validationState={this.state.errors && this.state.errors.name ? 'error' : null } controlId="formHorizontalName">
            <Col xs={3} componentClass={ControlLabel}>
              Assessment Name
            </Col>
            <Col xs={6}>
              <FormControl data-cy="assessment-name" name="name" type="text" placeholder="Assessment Name" value={this.props.assessment.name} onChange={this.props.changeAssessmentName}/>
              <FormControl.Feedback />
              { this.state.errors.name ? <HelpBlock data-cy='helpblock-name'>{this.state.errors.name}</HelpBlock> : '' }
            </Col>
            <Col>
              <h3 className="mt0 ta-center">Total Points: {this.getAssessmentPoints()}</h3>
            </Col>
          </FormGroup>

          <div data-cy="subsections">
            { subsections }
          </div>

          <div className='flex-row-spaced' style={{marginTop: '4rem' }}>
            <Button data-cy='add-subsection-button' style={{ marginRight: '10px', height: '40px' }} onClick={this.props.addSubsection}>
              <Glyphicon glyph={'plus-sign'}/>
            </Button>
            <Panel data-cy='add-subsection' className='m0 full-width'>
              <Panel.Heading>
                <Panel.Title>Add Subsection</Panel.Title>
              </Panel.Heading>
            </Panel>
          </div>

          <FormGroup className='mx-auto' style={{ marginTop: '5rem' }}>
            <Col>
              <div style={{ width: 'auto' }} className={'text-right ml-auto mr1'}>
                <Button data-cy="cancel-assessment" onClick={this.onCancel}>Cancel</Button>
                <Button
                  data-cy="save-assessment"
                  className='primary ml1'
                  type="submit"
                >
                  Save Assessment
                </Button>
              </div>
            </Col>
          </FormGroup>
        </Form>

        <ToastContainer
          position="top-center"
          autoClose={1500}
          hideProgressBar
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnVisibilityChange={false}
          draggablePercent={80}
          pauseOnHover={false}
        />
      </div>
    );
  }
}

EditAssessment.defaultProps = {
  isCreating: true
};

EditAssessment.propTypes = {
  assessment: PropTypes.shape(
    {
      name: PropTypes.string,
      subsections: PropTypes.array
    }
  ),
  isCreating: PropTypes.bool
};

const mapStateToProps = state => {
  return ({
    ...state.createAssessment
  });
};

const mapDispatchToProps = dispatch => ({
  clearForm: () => dispatch(clearAssessmentForm()),
  changeAssessmentName: (event) => dispatch(changeAssessmentName({ value: event.target.value })),
  addSubsection: () => dispatch(addSubsection()),
  changeSubsectionTitle: (value, index) => dispatch(changeSubsectionTitle({value, index})),
  removeSubsection: (i) => dispatch(removeSubsection(i))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(EditAssessment));



// WEBPACK FOOTER //
// ./src/components/assessments/assessment-form/EditAssessment.js